/**
 * @fileoverview Real-time WebSocket Client for the Admin Operator Console (/admin Tile ID 50) in the "Cronómetro" dynamic.
 * Connects via SignalRService native WebSockets with role 'superadmin' or 'admin'.
 * Subscribes to live connection stats, spectator leaderboard updates, and authoritative game states.
 * Exposes strictly-typed control methods to direct stadium game phases.
 * 
 * @module utilities/WS/Admin/CronometerAdminWS
 */

import SignalRService from '../SignalRService';

// =============================================================================
// INTERNAL HANDLER REFERENCES
// =============================================================================

let registeredMessageHandler = null;
let registeredConnectedHandler = null;
let registeredReconnectedHandler = null;
let registeredReconnectingHandler = null;
let registeredCloseHandler = null;
let registeredErrorHandler = null;

// Individual event listeners map
const adminEventCallbacks = {
    crono_connection_stats: [],
    crono_update_spectator: [],
    crono_game_state: [],
    crono_start_phase: [],
    crono_end_phase: [],
    crono_reset_game: [],
    crono_screen_mode: []
};

/**
 * Subscribes a callback to a specific admin Cronometer event.
 * 
 * @function OnAdminCronometerEvent
 * @param {string} eventName - 'crono_connection_stats', 'crono_update_spectator', 'crono_game_state', etc.
 * @param {Function} callback
 */
export const OnAdminCronometerEvent = (eventName, callback) => {
    if (adminEventCallbacks[eventName] && typeof callback === 'function') {
        adminEventCallbacks[eventName].push(callback);
    }
};

/**
 * Unsubscribes a callback from a specific admin Cronometer event.
 * 
 * @function OffAdminCronometerEvent
 * @param {string} eventName
 * @param {Function} callback
 */
export const OffAdminCronometerEvent = (eventName, callback) => {
    if (adminEventCallbacks[eventName]) {
        if (!callback) {
            adminEventCallbacks[eventName] = [];
        } else {
            adminEventCallbacks[eventName] = adminEventCallbacks[eventName].filter(cb => cb !== callback);
        }
    }
};

const notifyAdminEventCallbacks = (eventName, data) => {
    if (adminEventCallbacks[eventName]) {
        adminEventCallbacks[eventName].forEach(cb => {
            try {
                cb(data);
            } catch (e) {
                // Ignore listener exceptions
            }
        });
    }
};

/**
 * Establishes a persistent real-time WebSocket connection for the Cronometer Admin Console.
 * 
 * @function ConnectWebSocketForCronometerAdmin
 * @param {Function} [onIncomingMessage=()=>{}] - General handler for all received envelopes.
 * @param {Function} [onStatusChange=()=>{}] - Callback for connection status ('connecting', 'connected', 'reconnecting', 'error', 'closed').
 * @param {string} [adminRole='superadmin'] - Connection role ('superadmin' or 'admin').
 * @returns {Promise<void>}
 */
export const ConnectWebSocketForCronometerAdmin = async (
    onIncomingMessage = () => {},
    onStatusChange = () => {},
    adminRole = 'superadmin'
) => {
    onStatusChange('connecting');

    // Remove previously attached handlers
    if (registeredMessageHandler) SignalRService.off('message', registeredMessageHandler);
    if (registeredConnectedHandler) SignalRService.off('connected', registeredConnectedHandler);
    if (registeredReconnectedHandler) SignalRService.off('reconnected', registeredReconnectedHandler);
    if (registeredReconnectingHandler) SignalRService.off('reconnecting', registeredReconnectingHandler);
    if (registeredCloseHandler) SignalRService.off('close', registeredCloseHandler);
    if (registeredErrorHandler) SignalRService.off('error', registeredErrorHandler);

    registeredMessageHandler = (messageEnvelope) => {
        if (!messageEnvelope) return;

        const eventType = (messageEnvelope.type || messageEnvelope.Type || '').toLowerCase();
        const payload = messageEnvelope.payload !== undefined
            ? messageEnvelope.payload
            : (messageEnvelope.data !== undefined ? messageEnvelope.data : messageEnvelope);

        // Dispatch typed events
        switch (eventType) {
            case 'crono_connection_stats':
                notifyAdminEventCallbacks('crono_connection_stats', payload);
                break;

            case 'crono_update_spectator':
                notifyAdminEventCallbacks('crono_update_spectator', payload);
                break;

            case 'crono_game_state':
                notifyAdminEventCallbacks('crono_game_state', payload);
                break;

            case 'crono_start_phase':
                notifyAdminEventCallbacks('crono_start_phase', payload);
                break;

            case 'crono_end_phase':
                notifyAdminEventCallbacks('crono_end_phase', payload);
                break;

            case 'crono_reset_game':
                notifyAdminEventCallbacks('crono_reset_game', payload);
                break;

            case 'crono_screen_mode':
                notifyAdminEventCallbacks('crono_screen_mode', payload);
                break;

            default:
                break;
        }

        if (typeof onIncomingMessage === 'function') {
            onIncomingMessage(messageEnvelope);
        }
    };

    registeredConnectedHandler = () => {
        onStatusChange('connected');
    };

    registeredReconnectedHandler = () => {
        onStatusChange('connected');
    };

    registeredReconnectingHandler = (info) => {
        onStatusChange('reconnecting', info);
    };

    registeredCloseHandler = (closeEvent) => {
        onStatusChange('closed', closeEvent);
    };

    registeredErrorHandler = (err) => {
        onStatusChange('error', err);
    };

    SignalRService.on('message', registeredMessageHandler);
    SignalRService.on('connected', registeredConnectedHandler);
    SignalRService.on('reconnected', registeredReconnectedHandler);
    SignalRService.on('reconnecting', registeredReconnectingHandler);
    SignalRService.on('close', registeredCloseHandler);
    SignalRService.on('error', registeredErrorHandler);

    try {
        await SignalRService.connect({ tipo: adminRole || 'superadmin' });
    } catch (connectionError) {
        onStatusChange('error', connectionError);
    }
};

// =============================================================================
// TYPED OPERATOR CONTROL COMMANDS
// =============================================================================

/**
 * Commands the stadium screen (/screen) to display the promotional QR cover mode.
 * 
 * @function ShowCover
 * @returns {boolean} True if command was transmitted.
 */
export const ShowCover = () => {
    return SignalRService.send({
        type: 'crono_show_cover'
    });
};

/**
 * Commands the stadium screen (/screen) to switch to the active interactive game scene.
 * 
 * @function ShowGame
 * @returns {boolean} True if command was transmitted.
 */
export const ShowGame = () => {
    return SignalRService.send({
        type: 'crono_show_game'
    });
};

/**
 * Reveals the target number Z, margin and intermitente mode on the stadium screen before launching the phase.
 * 
 * @function ShowObjective
 * @param {number|string} z - Target time in seconds (e.g. 9).
 * @param {number|string} [margin=0.10] - Tolerance margin in seconds (e.g. 0.10).
 * @param {boolean} [intermitente=false] - Whether display intermittently blinks during gameplay.
 * @returns {boolean} True if command was transmitted.
 */
export const ShowObjective = (z, margin = 0.10, intermitente = false) => {
    return SignalRService.send({
        type: 'crono_show_objective',
        z: Number(z),
        margin: Number(margin),
        intermitente: Boolean(intermitente)
    });
};

/**
 * Starts an authoritative gameplay phase, triggering the 3-second synchronized countdown
 * on the stadium screen and mobile devices.
 * 
 * @function StartPhase
 * @param {number|string} fase - Phase number (1, 2, 3, etc.).
 * @param {number|string} z - Target time in seconds.
 * @param {number|string} [margin=0.10] - Tolerance margin in seconds.
 * @param {boolean} [intermitente=false] - Intermittent blink mode flag.
 * @returns {boolean} True if command was transmitted.
 */
export const StartPhase = (fase, z, margin = 0.10, intermitente = false) => {
    return SignalRService.send({
        type: 'crono_start_phase',
        fase: Number(fase),
        z: Number(z),
        margin: Number(margin),
        intermitente: Boolean(intermitente)
    });
};

/**
 * Ends the currently active phase, disqualifying omitting eligible players and triggering batch database persistence.
 * 
 * @function EndPhase
 * @returns {boolean} True if command was transmitted.
 */
export const EndPhase = () => {
    return SignalRService.send({
        type: 'crono_end_phase'
    });
};

/**
 * Changes the target phase pointer on the server.
 * 
 * @function ChangePhase
 * @param {number|string} fase - The new phase number to set.
 * @returns {boolean} True if command was transmitted.
 */
export const ChangePhase = (fase) => {
    return SignalRService.send({
        type: 'crono_change_phase',
        fase: Number(fase)
    });
};

/**
 * Resets the entire Cronometer dynamic in RAM and database back to Phase 1.
 * 
 * @function ResetGame
 * @returns {boolean} True if command was transmitted.
 */
export const ResetGame = () => {
    return SignalRService.send({
        type: 'crono_reset'
    });
};

/**
 * Disconnects the admin WebSocket client and unbinds event listeners.
 * 
 * @function DisconnectWebSocketForCronometerAdmin
 * @returns {Promise<void>}
 */
export const DisconnectWebSocketForCronometerAdmin = async () => {
    if (registeredMessageHandler) {
        SignalRService.off('message', registeredMessageHandler);
        registeredMessageHandler = null;
    }
    if (registeredConnectedHandler) {
        SignalRService.off('connected', registeredConnectedHandler);
        registeredConnectedHandler = null;
    }
    if (registeredReconnectedHandler) {
        SignalRService.off('reconnected', registeredReconnectedHandler);
        registeredReconnectedHandler = null;
    }
    if (registeredReconnectingHandler) {
        SignalRService.off('reconnecting', registeredReconnectingHandler);
        registeredReconnectingHandler = null;
    }
    if (registeredCloseHandler) {
        SignalRService.off('close', registeredCloseHandler);
        registeredCloseHandler = null;
    }
    if (registeredErrorHandler) {
        SignalRService.off('error', registeredErrorHandler);
        registeredErrorHandler = null;
    }

    Object.keys(adminEventCallbacks).forEach(key => {
        adminEventCallbacks[key] = [];
    });

    try {
        await SignalRService.disconnect();
    } catch (e) {
        // Ignore disconnect errors
    }
};

export default {
    ConnectWebSocketForCronometerAdmin,
    DisconnectWebSocketForCronometerAdmin,
    OnAdminCronometerEvent,
    OffAdminCronometerEvent,
    ShowCover,
    ShowGame,
    ShowObjective,
    StartPhase,
    EndPhase,
    ChangePhase,
    ResetGame
};
